import React, { useState, useEffect } from 'react';
import { LayoutDashboard } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { StatTile, MiniBarChart } from '../../components/charts/MiniCharts';
import {
  analytics as analyticsApi,
  reports as reportsApi,
  facilityHeatmap,
  type AnalyticsOverview,
  type AnalyticsTrend,
  type ExecutiveSummary,
  type HeatmapCell,
} from '../../services/api';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const fmtAmount = (n: number | undefined) =>
  `₹${((n ?? 0) / 100000).toFixed(1)}L`;

const AnalyticsExecutivePage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [trends, setTrends] = useState<AnalyticsTrend[]>([]);
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapCell[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    Promise.all([
      analyticsApi.overview(societyId),
      analyticsApi.trends(societyId),
      reportsApi.executiveSummary(societyId),
      facilityHeatmap.get(societyId),
    ])
      .then(([o, t, s, h]) => { setOverview(o); setTrends(t); setSummary(s); setHeatmap(h.cells); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [societyId]);

  const maxCount = Math.max(1, ...heatmap.map(c => c.bookingCount));
  const cellCount = (day: string, hour: number) => heatmap.find(c => c.dayOfWeek === day && c.hourOfDay === hour)?.bookingCount ?? 0;

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Executive Dashboard
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Society-wide KPIs across finance, collections, helpdesk and facilities.</p>
      </div>

      {loading ? (
        <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
          <span style={{ width: 24, height: 24, border: '2px solid var(--nx-border)', borderTopColor: '#1A6CF6', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatTile label="Net Surplus" value={fmtAmount(summary?.netSurplus)} sub={`Income ${fmtAmount(summary?.totalIncome)} − Expense ${fmtAmount(summary?.totalExpense)}`} accent="#10B981" />
            <StatTile label="Collection Rate" value={`${(summary?.collectionRate ?? 0).toFixed(1)}%`} sub={`${summary?.defaulterCount ?? 0} units in default`} accent="#1A6CF6" />
            <StatTile label="Open Tickets" value={String(summary?.openTickets ?? 0)} sub={`${summary?.breachedTickets ?? 0} SLA breached`} accent="#D97706" />
            <StatTile label="Facility Utilisation" value={`${(summary?.avgFacilityUtilization ?? 0).toFixed(1)}%`} sub="Avg. across facilities, last 30 days" accent="#6D28D9" />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <StatTile label="Total Units" value={String(overview?.totalUnits ?? 0)} sub={`${overview?.occupiedUnits ?? 0} occupied`} />
            <StatTile label="Active Residents" value={String(overview?.activeResidents ?? 0)} />
            <StatTile label="Visitors Today" value={String(overview?.visitorsToday ?? 0)} sub={`${overview?.pendingVisitors ?? 0} pending`} />
            <StatTile label="Dues Collected" value={fmtAmount(overview?.duesCollected)} sub={`of ${fmtAmount(overview?.duesTarget)} target`} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="nx-card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
                Revenue Trend
              </h3>
              <MiniBarChart
                data={trends.map(t => ({ label: t.month, value: t.duesCollected }))}
                color="#10B981"
                formatValue={v => `₹${(v / 1000).toFixed(0)}k`}
              />
            </div>
            <div className="nx-card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
                Tickets Raised
              </h3>
              <MiniBarChart
                data={trends.map(t => ({ label: t.month, value: t.tickets }))}
                color="#D97706"
              />
            </div>
          </div>

          <div className="nx-card" style={{ padding: 20, marginTop: 16 }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
              Facility Utilisation Heatmap (last 90 days)
            </h3>
            {heatmap.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>No facility bookings in this period yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${HOURS.length}, 20px)`, gap: 2, minWidth: 560 }}>
                  <div />
                  {HOURS.map(h => (
                    <div key={h} style={{ fontSize: 8, color: 'var(--nx-text-muted)', textAlign: 'center' }}>{h}</div>
                  ))}
                  {DAYS.map(day => (
                    <React.Fragment key={day}>
                      <div style={{ fontSize: 10, color: 'var(--nx-text-secondary)', display: 'flex', alignItems: 'center' }}>
                        {day.slice(0, 3)}
                      </div>
                      {HOURS.map(hour => {
                        const count = cellCount(day, hour);
                        const intensity = count / maxCount;
                        return (
                          <div
                            key={hour}
                            title={`${day} ${hour}:00 — ${count} bookings`}
                            style={{
                              width: 20, height: 20, borderRadius: 3,
                              background: count === 0 ? 'var(--nx-surface-3)' : `rgba(26, 108, 246, ${0.15 + intensity * 0.85})`,
                            }}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!societyId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--nx-text-muted)', fontSize: 13 }}>
          <LayoutDashboard size={16} /> No society context available.
        </div>
      )}
    </PageLayout>
  );
};

export default AnalyticsExecutivePage;
